/**
 * blob-queue.ts — offline blob capture and upload (S3-T5).
 *
 * Enqueues Blob objects (signatures, photos) into the `blobs` IndexedDB store.
 * drainBlobs(ownerLocalId) uploads all pending blobs for a report to the active
 * storage provider (selected server-side; /uploads/firma tags the response):
 *   - cloudinary → POST multipart to Cloudinary → secure_url
 *   - r2         → PUT directly to the presigned URL → publicUrl
 *
 * Returns true only when ALL blobs for the report are uploaded (per-report atomicity).
 * One failure → false → report stays queued (design §5, spec Blob upload failure scenario).
 */
import { getDb } from "../stores/db.js";
import { httpPost } from "@/shared/lib/http-client.js";
import type { BlobQueueItem } from "../types.js";

// ─── /uploads/firma response (discriminated by provider) ──────────────────────

interface CloudinaryFirma {
  provider: "cloudinary";
  signature: string;
  timestamp: number;
  apiKey: string;
  cloudName: string;
  folder: string;
}

interface R2Firma {
  provider: "r2";
  url: string;
  method: "PUT";
  key: string;
  publicUrl: string;
}

type Firma = CloudinaryFirma | R2Firma;

// ─── IDB helpers ─────────────────────────────────────────────────────────────

export async function enqueueBlob(
  item: Omit<BlobQueueItem, "uploadState" | "attempts" | "remoteUrl">,
): Promise<void> {
  const db = await getDb();
  await db.put("blobs", {
    ...item,
    uploadState: "pending",
    attempts: 0,
  } satisfies BlobQueueItem);
}

export async function getBlobsForReport(ownerLocalId: string): Promise<BlobQueueItem[]> {
  const db = await getDb();
  return db.getAllFromIndex("blobs", "by-owner", ownerLocalId) as Promise<BlobQueueItem[]>;
}

export async function markUploaded(id: string, remoteUrl: string): Promise<void> {
  const db = await getDb();
  const existing = await db.get("blobs", id) as BlobQueueItem | undefined;
  if (!existing) return;
  await db.put("blobs", { ...existing, uploadState: "uploaded", remoteUrl });
}

export async function markBlobError(id: string): Promise<void> {
  const db = await getDb();
  const existing = await db.get("blobs", id) as BlobQueueItem | undefined;
  if (!existing) return;
  await db.put("blobs", { ...existing, attempts: existing.attempts + 1 });
}

/**
 * Delete all queued blobs for a report without uploading them. Used when the
 * blobs became unnecessary because their payload was resolved by another path
 * (e.g. AC firmas uploaded directly on an online finalize), so leaving them
 * queued would orphan them in IDB.
 */
export async function discardBlobs(ownerLocalId: string): Promise<void> {
  const db = await getDb();
  const blobs = await getBlobsForReport(ownerLocalId);
  await Promise.all(blobs.map((b) => db.delete("blobs", b.id)));
}

// ─── provider upload ───────────────────────────────────────────────────────────

/**
 * Upload one blob to the provider named in `firma`. Returns the public URL on
 * success, or null on any failure (caller marks the blob errored).
 */
async function uploadBlob(firma: Firma, blob: Blob, contentType: string): Promise<string | null> {
  if (firma.provider === "r2") {
    const res = await fetch(firma.url, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: blob,
    });
    return res.ok ? firma.publicUrl : null;
  }

  // cloudinary
  const form = new FormData();
  form.append("file", blob);
  form.append("api_key", firma.apiKey);
  form.append("timestamp", String(firma.timestamp));
  form.append("signature", firma.signature);
  form.append("folder", firma.folder);

  const res = await fetch(`https://api.cloudinary.com/v1_1/${firma.cloudName}/image/upload`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { secure_url?: string };
  return data.secure_url ?? null;
}

// ─── drain ────────────────────────────────────────────────────────────────────

/**
 * Upload all pending blobs for a report to R2.
 *
 * Per-report atomicity: returns true only when ALL blobs for ownerLocalId
 * are in state 'uploaded' after this call. Returns false if any upload fails.
 * Reports with no pending blobs return true immediately (no blobs = eligible for /sync).
 */
/**
 * Fire-and-forget telemetry: flag storage as degraded (or clear it) so the admin
 * panel-wide banner can warn admins. This must NEVER throw or block the drain
 * loop, so it is defensive against a synchronous throw AND a non-Promise return
 * (e.g. a narrowly-mocked httpPost in tests).
 */
function reportStorageDegraded(degraded: boolean): void {
  try {
    const p = httpPost("/storage/degraded", { degraded }) as unknown;
    if (p && typeof (p as Promise<unknown>).catch === "function") {
      (p as Promise<unknown>).catch(() => {});
    }
  } catch {
    /* telemetry is best-effort — never break draining */
  }
}

export async function drainBlobs(ownerLocalId: string): Promise<boolean> {
  const blobs = await getBlobsForReport(ownerLocalId);
  const pending = blobs.filter((b) => b.uploadState === "pending");

  if (pending.length === 0) return true;

  let allUploaded = true;

  for (const item of pending) {
    try {
      // Step 1: get a signed upload from the API (carries JWT via http-client).
      const folder = `${ownerLocalId}/${item.fieldPath}`;
      const contentType = item.blob.type || "application/octet-stream";
      const firma = await httpPost<Firma>("/uploads/firma", { folder, contentType });

      // Step 2: upload to the active provider; record the public URL on success.
      const remoteUrl = await uploadBlob(firma, item.blob, contentType);
      if (!remoteUrl) {
        await markBlobError(item.id);
        allUploaded = false;
        reportStorageDegraded(true);
        continue;
      }
      await markUploaded(item.id, remoteUrl);
    } catch {
      await markBlobError(item.id);
      allUploaded = false;
      reportStorageDegraded(true);
    }
  }

  // Note: we do NOT clear the degraded flag on a clean drain — that would fire a
  // telemetry request on every successful sync (the hot path). The flag is cleared
  // server-side when a PDF upload succeeds (GeneratePdfUseCase), which happens when
  // an admin opens a report after storage recovers. Erring toward a slightly-sticky
  // "contact support" banner is safer than clearing it too eagerly.
  return allUploaded;
}
